<script setup>
const { $tp } = useNuxtApp();
</script>

<template>
  <section class="sections-intro">
    <div class="sections-intro__container container">
      <div class="sections-intro__radial sections-intro__radial--1"></div>
      <div class="sections-intro__radial sections-intro__radial--2"></div>
      <div class="sections-intro__radial sections-intro__radial--3"></div>

      <div class="sections-intro__body">
        <CSpriteIcon
          v-for="item in 6"
          :key="item"
          class="sections-intro__icon sections-intro__icon--plus"
          :class="`sections-intro__icon--plus--${item}`"
          name="plus"
        />

        <div class="sections-intro__line sections-intro__line--1"></div>

        <div class="sections-intro__main-key">
          <div
            class="sections-intro__wrapper sections-intro__wrapper--main-key-text"
          >
            <h1 class="sections-intro__main-key-title">
              <span
                class="sections-intro__font sections-intro__font--main-key-title"
              >
                Your Backend for&nbsp;Frontline Travel
              </span>
            </h1>

            <p class="sections-intro__main-key-description">
              <span
                class="sections-intro__font sections-intro__font--main-key-description"
              >
                {{ $tp(`Every journey starts behind the scenes.`) }}
                <br />
                {{
                  $tp(`We create the digital solutions that help those who shape travel
                experiences — ensuring every adventure runs smoothly from start
                to finish.`)
                }}
              </span>
            </p>
          </div>

          <CPicture
            name="centurio_key_visual"
            class="sections-intro__image sections-intro__image--main-key-image"
          />
        </div>

        <div class="sections-intro__description">
          <div class="sections-intro__features">
            <BlockFeature
              class="sections-intro__feature"
              sprite-icon="icon_back-office"
              text="From simplifying complex back-office management to developing intuitive booking platforms and advanced data monitoring, our solutions enable companies to automate operations, optimize efficiency, and deliver exceptional user experiences."
            />

            <BlockFeature
              class="sections-intro__feature"
              sprite-icon="icon_managing"
              text="Whether you're managing routes, tracking real-time availability, or enhancing customer communication — Centour.io is your trusted partner behind every successful journey."
            />
          </div>

          <div class="sections-intro__awards">
            <UiCaptionUnderline
              align="end"
              class="sections-intro__awards-caption"
              caption="awards"
            />

            <div class="sections-intro__awards-list">
              <div class="sections-intro__award-placeholder"></div>

              <CPicture
                name="award1"
                alt="award1"
                class="sections-intro__award-image sections-intro__award-image--1"
              />

              <CPicture
                name="award2"
                alt="award2"
                class="sections-intro__award-image sections-intro__award-image--2"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.sections-intro {
  overflow: hidden;

  &__font {
    display: block;

    &--main-key-title {
      font-size: em(46);
      font-weight: 800;
      line-height: 105%;

      @include media-breakpoint-down(md) {
        font-size: em(36);
        line-height: 115%;
        letter-spacing: 0.01em;
      }

      @include media-breakpoint-down(sm) {
        font-size: em(30);
        line-height: 105%;
      }
    }

    &--main-key-description {
      font-family: $font-family-second;
      font-size: em(22);
      font-weight: 500;
      line-height: 115%;

      @include media-breakpoint-down(md) {
        font-size: em(18);
        line-height: 120%;
      }

      @include media-breakpoint-down(sm) {
        font-size: em(16);
      }
    }
  }

  &__icon {
    &--plus {
      position: absolute;
      width: em(40);
      height: em(40);
      pointer-events: none;
      opacity: 0.5;

      @include media-breakpoint-down(md) {
        width: em(32);
        height: em(32);
      }

      &--1 {
        right: em(520);
        bottom: em(60);

        @include media-breakpoint-down(md) {
          right: unset;
          bottom: em(48);
          left: em(12);
        }

        @include media-breakpoint-down(sm) {
          bottom: em(35);
          left: em(9);
        }
      }

      &--2 {
        right: em(520);
        bottom: em(140);

        @include media-breakpoint-down(md) {
          right: unset;
          bottom: em(113);
          left: em(12);
        }

        @include media-breakpoint-down(sm) {
          right: unset;
          bottom: em(126);
          left: em(9);
        }
      }

      &--3 {
        right: em(520);
        bottom: em(200);

        @include media-breakpoint-down(md) {
          right: unset;
          bottom: em(161);
          left: em(12);
        }

        @include media-breakpoint-down(sm) {
          right: unset;
          bottom: em(170);
          left: em(9);
        }
      }

      &--4 {
        right: 0;
        bottom: em(60);

        @include media-breakpoint-down(md) {
          right: em(12);
          bottom: em(48);
        }

        @include media-breakpoint-down(sm) {
          right: em(9);
          bottom: em(35);
        }
      }

      &--5 {
        right: 0;
        bottom: em(140);

        @include media-breakpoint-down(md) {
          right: em(12);
          bottom: em(113);
        }

        @include media-breakpoint-down(sm) {
          right: em(9);
          bottom: em(126);
        }
      }

      &--6 {
        right: 0;
        bottom: em(200);

        @include media-breakpoint-down(md) {
          right: em(12);
          bottom: em(161);
        }

        @include media-breakpoint-down(sm) {
          right: em(9);
          bottom: em(170);
        }
      }
    }
  }

  &__radial {
    position: absolute;
    opacity: 0.7;

    &--1 {
      top: em(40);
      left: em(610);
      width: em(500);
      height: em(643);
      background: radial-gradient(
        50% 50% at 50% 50%,
        #ffffff 0%,
        rgb(255 255 255 / 0%) 100%
      );
      transform: rotate(38deg);

      @include media-breakpoint-down(md) {
        top: em(10);
        left: em(320);
        width: em(400);
        height: em(514);
      }
    }

    &--2 {
      top: em(-20);
      left: em(90);
      width: em(500);
      height: em(764);
      background: radial-gradient(
        50% 50% at 50% 50%,
        #ffffff 0%,
        rgb(255 255 255 / 0%) 100%
      );
      transform: rotate(-48deg);

      @include media-breakpoint-down(md) {
        top: em(-40);
        left: em(-70);
        width: em(400);
        height: em(611);
      }
    }

    &--3 {
      position: absolute;
      bottom: em(12);
      left: em(50);
      width: em(862);
      height: em(582);
      background: radial-gradient(
        50% 50% at 50% 50%,
        #ffffff 0%,
        rgb(255 255 255 / 0%) 100%
      );
      opacity: 0.6;

      @include media-breakpoint-up(md) {
        display: none;
      }
    }
  }

  &__container {
    position: relative;

    @include media-breakpoint-down(md) {
      max-width: em(504);
    }
  }

  &__body {
    position: relative;
    padding: em(160) 0 em(41) em(40);

    @include media-breakpoint-down(md) {
      padding: em(125) 0 em(34);
    }

    @include media-breakpoint-down(sm) {
      padding: em(102) 0 em(34);
    }
  }

  &__line {
    position: absolute;
    top: 0;
    z-index: 1;
    width: 1px;
    height: 100%;
    background-image: url('#{$icons-path}/dash-vert.svg');
    background-repeat: repeat-y;
    background-size: em(3);

    &--1 {
      left: 50%;
      transform: translateX(-50%);

      @include media-breakpoint-down(md) {
        display: none;
      }
    }
  }

  &__main-key {
    display: flex;
    justify-content: space-between;

    @include media-breakpoint-down(md) {
      flex-direction: column;
    }
  }

  &__wrapper {
    &--main-key-text {
      width: em(480);
      padding-top: em(60);

      @include media-breakpoint-down(md) {
        width: auto;
        padding: 0 em(43);
      }

      @include media-breakpoint-down(sm) {
        padding: 0 em(18);
      }
    }
  }

  &__image {
    &--main-key-image {
      width: em(560);
      height: em(360);

      @include media-breakpoint-down(md) {
        width: 100%;
        max-width: em(448);
        height: auto;
        margin: 0 auto;
      }
    }
  }

  &__main-key-title {
    color: $text-dark-main;
  }

  &__main-key-description {
    margin-top: em(22);
    color: $text-dark-secondary;

    @include media-breakpoint-down(md) {
      margin-top: em(15);
      margin-bottom: em(30);
    }

    @include media-breakpoint-down(sm) {
      margin-top: em(10);
    }
  }

  &__description {
    display: flex;
    justify-content: space-between;
    margin-top: em(20);

    @include media-breakpoint-down(md) {
      flex-direction: column;
    }
  }

  &__features {
    display: grid;
    grid-template-columns: em(230) em(230);
    gap: em(20);

    @include media-breakpoint-down(md) {
      grid-template-columns: repeat(auto-fit, minmax(#{em(212)}, 1fr));
      gap: em(20) em(15);
      padding: 0 em(12);
    }
  }

  &__feature {
  }

  &__awards {
    width: em(560);
    padding: em(40);

    @include media-breakpoint-down(md) {
      width: 100%;
      padding: em(30) em(42);
      margin-top: em(34);
    }

    @include media-breakpoint-down(sm) {
      width: 100%;
      padding: em(30) em(37);
      margin-top: em(20);
    }
  }

  &__awards-caption {
    width: fit-content;
    margin-left: auto;
  }

  &__awards-list {
    display: flex;
    align-items: center;
    margin-top: em(20);

    @include media-breakpoint-down(md) {
      margin-top: em(16);
    }

    @include media-breakpoint-down(sm) {
      margin-top: em(26);
    }
  }

  &__award-placeholder {
    width: 100%;
    height: em(42);
    margin-right: em(24);
    background: url('#{$images-path}/grid_tile_6-6.webp') repeat-x;

    @include media-breakpoint-down(md) {
      height: em(33);
      margin-right: em(19);
    }
  }

  &__award-image {
    &--1 {
      flex-shrink: 0;
      width: em(80);
      height: em(80);
      margin-right: em(10);

      @include media-breakpoint-down(md) {
        width: em(64);
        height: em(64);
        margin-right: em(8);
      }
    }

    &--2 {
      flex-shrink: 0;
      width: em(86);
      height: em(80);

      @include media-breakpoint-down(md) {
        width: em(68);
        height: em(64);
      }
    }
  }
}
</style>
